/**
 * INTRO MODEL (B6): pure. The intro's words and the skip button as a function of
 * the choreographer's `intro:beat` events plus three timed moments of our own (spec §E.7).
 *
 *   t (ms)  event        view
 *   0       start        (nothing yet; the loader fades above)
 *   400     line1        "Welcome to Ládera City"  in (.ld-intro p.is-on: 1200 drift / rise 1400 settle)
 *   600     skip-in      "Skip the introduction" fades in (240 settle)          ← start + 600
 *   2900    line1-out    line 1 out (700, .is-gone)                               ← line1 + 2500
 *   3300    line2        line 1 out if still on · "Lohegaon, Pune" in
 *   5100    line2-out    line 2 out (700)                                        ← line2 + 1800
 *   8300    ui           skip leaves (168 leave); the app's chrome arrives (phase → explore)
 *   9200    done         the overlay is finished
 *
 * The timed moments are scheduled from the beat they follow (not from t0), so the words always sit
 * in the right order around the map beats even when a busy main thread stretches the timeline.
 * "fly", "site" and "rings" are safety nets: any line still on screen leaves.
 *
 * skip (user, from 600 ms until the ui beat): the lines leave on the 420 ms cross-fade, the button
 * leaves, and the runner aborts the choreographer's intro (which jumps to its final state).
 */
import type { IntroBeat } from "@/contracts/bus";

export type IntroMode = "full" | "short";
export type LineState = "idle" | "on" | "gone";
export type SkipState = "hidden" | "shown" | "gone";
/** Beats from the choreographer, plus our own timed moments and the user's skip. */
export type IntroEvent = IntroBeat | "line1-out" | "line2-out" | "skip-in" | "skip";

export interface IntroView {
  mode: IntroMode;
  /** The "start" beat has played. */
  started: boolean;
  line1: LineState;
  line2: LineState;
  skip: SkipState;
  /** The lines leave on the skip's 420 ms cross-fade instead of the 700 ms exit. */
  fast: boolean;
  /** The "ui" beat has played (the chrome is arriving). */
  ui: boolean;
  /** The intro is over (the choreographer resolved, or the watchdog fired). */
  done: boolean;
  skipped: boolean;
  /** The land has left the opening night (the sunrise beat played, or the map jumped to its final state). */
  sunrise: boolean;
}

/** Copy deck §F (binding). */
export const INTRO_COPY = {
  line1: "Welcome to Ládera City",
  line2: "Lohegaon, Pune",
  skip: "Skip the introduction",
} as const;

/** §E.7 timings the overlay owns (the map beats are the choreographer's INTRO_BEATS). */
export const INTRO_TIMING = {
  /** Skip appears this long after "start" ("Skip is available from 600 ms"). */
  skipAfterMs: 600,
  /** line1 at 400 → out at 2900. */
  line1HoldMs: 2500,
  /** line2 at 3300 → out at 5100. */
  line2HoldMs: 1800,
  /** .ld-intro p.is-gone */
  lineOutMs: 700,
  /** Short intro and skip: everything cross-fades over 420 drift. */
  crossFadeMs: 420,
  /** Skip button in: 240 settle; out: 0.7 × 240 leave. */
  skipInMs: 240,
  skipOutMs: 168,
  /** The full intro's last beat. */
  fullMs: 9200,
  /** Watchdog: finish anyway this long after the expected end (a destroyed map never resolves). */
  graceMs: 3000,
} as const;

export function initialView(mode: IntroMode): IntroView {
  return { mode, started: false, line1: "idle", line2: "idle", skip: "hidden", fast: false, ui: false, done: false, skipped: false, sunrise: mode === "short" };
}

const out = (s: LineState): LineState => (s === "on" ? "gone" : s);

/** The skip is offered from 600 ms until the ui beat, in the full intro only. */
export function canSkip(v: IntroView): boolean {
  return v.mode === "full" && v.started && v.skip === "shown" && !v.ui && !v.done && !v.skipped;
}

/** When the intro should be over at the latest (watchdog), in ms from "start". */
export function expectedEndMs(mode: IntroMode): number {
  return (mode === "full" ? INTRO_TIMING.fullMs : INTRO_TIMING.crossFadeMs) + INTRO_TIMING.graceMs;
}

/** Pure transition. Unknown or out-of-order events leave the view unchanged. */
export function reduceIntro(v: IntroView, e: IntroEvent): IntroView {
  if (v.done) return v;
  const full = v.mode === "full" && !v.skipped;
  switch (e) {
    case "start":
      return v.started ? v : { ...v, started: true };
    case "line1":
      return full && v.line1 === "idle" && !v.ui ? { ...v, line1: "on" } : v;
    case "line1-out":
      return v.line1 === "on" ? { ...v, line1: "gone" } : v;
    case "line2":
      if (!full || v.ui) return v.line1 === "on" ? { ...v, line1: "gone" } : v;
      return { ...v, line1: out(v.line1), line2: v.line2 === "idle" ? "on" : v.line2 };
    case "line2-out":
      return v.line2 === "on" ? { ...v, line2: "gone" } : v;
    case "sunrise":
      return v.sunrise ? v : { ...v, sunrise: true };
    case "fly":
    case "site":
    case "rings": {
      const w = v.sunrise ? v : { ...v, sunrise: true }; // safety net: the sunrise is behind these beats
      return w.line1 === "on" || w.line2 === "on" ? { ...w, line1: out(w.line1), line2: out(w.line2) } : w;
    }
    case "skip-in":
      return full && v.started && v.skip === "hidden" && !v.ui ? { ...v, skip: "shown" } : v;
    case "skip":
      if (!canSkip(v)) return v;
      return { ...v, skipped: true, fast: true, sunrise: true, line1: out(v.line1), line2: out(v.line2), skip: "gone" };
    case "ui":
      if (v.ui) return v;
      return { ...v, ui: true, sunrise: true, line1: out(v.line1), line2: out(v.line2), skip: v.skip === "shown" ? "gone" : v.skip };
    case "done":
      return { ...v, done: true, line1: out(v.line1), line2: out(v.line2), skip: v.skip === "shown" ? "gone" : v.skip };
    default:
      return v; // contours: a map-only beat
  }
}
